import lodash, {cloneDeep} from "lodash";
import {FacetUtils} from "../facets/facet-utils";
import {FacetStats, OverviewFilters, QuickView} from "../facets/facet-filter/facet-filter.component";
import {FacetedOverviewFilters} from "./overview.component";
import {Observable} from "rxjs";
import {AfterViewInit, Directive, signal} from "@angular/core";
import {
  DateTimeRange,
  FacetedListSearch,
  FacetFilter,
  FacetResult,
  FacetValueResult
} from "@portbase/bezoekschip-service-typescriptmodels";
import {CheckboxSelectionState, mergeIfNotExists, sendQuery} from "../../../common/utils";
import {map, tap} from "rxjs/operators";
import {sendFacetsQuery} from "../../../common/handlers/app-common-utils";
import {DateFieldRange, MomentDateFieldRange} from "../../../common/date/date-range/date-field-range";
import moment from "moment";

@Directive()
export abstract class FacetedOverview<RESULT, FILTERS extends FacetedOverviewFilters<any>> implements AfterViewInit {
  facetUtils = FacetUtils;
  private localStorageKey: string;
  filters: FILTERS;
  from: number = 0;
  maxItems: number = 100;
  endReached: boolean;
  loading: boolean;
  facetsLoading: boolean;
  loadCount: number = 0;
  minCharactersTerm: number = 3;
  searchTerm: string;
  data: any[];
  hiddenFacets: string[] = [];
  facetOrdering: string[] = [];
  otherFacetNames: string[] = [];
  facets: FacetStats[];
  otherFacets: FacetStats[];
  allFacets: FacetStats[];
  overviewFiltersBeforeSearch: FILTERS;
  protected hasActiveFacetFilters: boolean;

  ranges: MomentDateFieldRange[] = [
    {
      label: 'Today',
      start: moment().startOf('day'),
      end: moment().add(1, "day").startOf('day').subtract(1, 'day')
    },
    {
      label: 'Yesterday',
      start: moment().subtract(1, 'day').startOf('day'),
      end: moment().startOf('day').subtract(1, 'day')
    },
    {
      label: 'This week',
      start: moment().startOf('week'),
      end: moment().add(1, "week").startOf('week').subtract(1, 'day')
    },
    {
      label: 'Last 7 days',
      start: moment().subtract(7, 'day').startOf('day'),
      end: moment().add(1, "day").startOf('day'),
      default: true
    },
    {
      label: 'This month',
      start: moment().startOf('month'),
      end: moment().add(1, "month").startOf('month').subtract(1, 'day')
    },
    {
      label: 'Last 3 months',
      start: moment().subtract(3, 'month').startOf('month'),
      end: moment().add(1, "month").startOf('month').subtract(1, 'day')
    },
    {
      label: 'Last half year',
      start: moment().subtract(6, 'month').startOf('month'),
      end: moment().add(1, "month").startOf('month').subtract(1, 'day')
    }
  ];

  nameFormatter: (name: string) => string;
  getValueFormatter: (name: string) => (value: string) => string;
  filterFacetValues: (name: string, values: FacetValueResult[]) => FacetValueResult[];
  getValuesSelectedCallback: (name: string) => (facet: FacetStats, selectedValues: string[]) => string[];
  isSelectable: (record: RESULT) => boolean;

  abstract getDataQuery(term: string): DataQuery<FacetedListSearch<any>>;
  abstract renderRecords(result: any, append: boolean);
  abstract trackByRecord(index: number, record: RESULT): any;
  abstract getHiddenFacets(): string[];

  protected constructor(localStorageKey?: string, protected quickViews?: QuickView[], private defaultQuickView?: QuickView) {
    this.setLocalStorageKey(localStorageKey);
  }

  protected setLocalStorageKey(localStorageKey: string) {
    this.localStorageKey = localStorageKey;
    this.filters = this.getFiltersFromLocalStorage();
    this.filters.overviewFilters.selectedQuickView = this.quickViews.filter(q => !q.hidden || !q.hidden())
        .find(q => q.code === (this.filters.overviewFilters.selectedQuickView?.code || this.defaultQuickView.code))
      || this.defaultQuickView;
  }

  ngAfterViewInit(): void {
    this.loadData();
  }

  getData(term: string): Observable<any> {
    if (!this.filters.overviewFilters) {
      this.filters.overviewFilters = FacetUtils.defaultOverviewFilters();
    }
    this.loading = true;
    this.saveFiltersInLocalStorage(this.filters);
    const query = this.getDataQuery(term);
    return sendQuery(query.className, query.payload, {showSpinner: true})
      .pipe(tap(() => {
        this.searchTerm = term;
        this.loading = false;
        this.loadCount++;
      }));
  }

  get isEmpty() {
    return !this.loading && (!this.data || this.data.length === 0);
  }

  protected getFacetFilters() {
    return Object.entries(this.getMergedFacetFilters())
      .map(e => (<FacetFilter>{
        facetName: e[0],
        values: lodash.isArray(e[1]) ? e[1]?.filter(v => v) : [e[1]]
      })).filter(f => f.values?.length > 0);
  }

  protected getMergedFacetFilters() {
    let mergedFilters:  { [key: string]: string[] } = {};
    const quickViewFilters = cloneDeep(this.filters.overviewFilters.selectedQuickView?.facets || {});
    const facetsFiltersFn = this.filters.overviewFilters.selectedQuickView?.facetsFn ? cloneDeep(this.filters.overviewFilters.selectedQuickView.facetsFn() || {}) : {};
    const overviewFilters = cloneDeep(this.filters.overviewFilters.facets || {});
    const hiddenFacets = cloneDeep(this.filters.overviewFilters.hiddenFacets || {});
    lodash.mergeWith(mergedFilters, overviewFilters, quickViewFilters, facetsFiltersFn, hiddenFacets, mergeIfNotExists);
    return mergedFilters;
  }

  // Parent-owned, immutable selection state keyed by the record's stable id (the same id
  // used by trackByRecord). Selection is NOT stored on the model objects anymore, so it flows
  // one-way into the OnPush item components and is toggled back up via an output, keeping
  // change detection correct. Update ONLY by setting a new Set reference.
  readonly selectedIds = signal<ReadonlySet<string>>(new Set<string>());

  protected getRecordId(record: RESULT): string {
    return this.trackByRecord(0, record);
  }

  isRecordSelected = (record: RESULT): boolean => this.selectedIds().has(this.getRecordId(record));

  toggleRecordSelection = (record: RESULT): void => {
    if (this.isSelectable && !this.isSelectable(record)) {
      return;
    }
    const id = this.getRecordId(record);
    this.selectedIds.update(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  protected clearSelection(): void {
    this.selectedIds.set(new Set<string>());
  }

  get allSelectionState(): CheckboxSelectionState {
    const data = this.data || [];
    return data.length
      ? data.every(d => this.isRecordSelected(d))
        ? CheckboxSelectionState.selected : data.some(d => this.isRecordSelected(d))
          ? CheckboxSelectionState.indeterminate : CheckboxSelectionState.unselected : CheckboxSelectionState.unselected;
  }

  set allSelectionState(state: CheckboxSelectionState) {
    const selectable = (this.data || []).filter(d => this.isSelectable(d));
    this.selectedIds.update(prev => {
      const next = new Set(prev);
      selectable.forEach(d => {
        const id = this.getRecordId(d);
        if (state === CheckboxSelectionState.selected) {
          next.add(id);
        } else if (state === CheckboxSelectionState.unselected) {
          next.delete(id);
        }
      });
      return next;
    });
  }

  protected setFacetsFromResult(facets: { [p: string]: FacetValueResult[] }) {
    if (this.from && this.from > 0) {
      return;
    }
    const currentFacetFilters = Object.keys(this.filters?.overviewFilters?.facets || {});
    this.allFacets = Object.entries(facets)
      .map(e => {
        const hasCurrentFacetValue = currentFacetFilters.includes(e[0]);
        const currentValue = hasCurrentFacetValue && this.allFacets?.find(f => f.name === e[0]);
        if (currentValue) {
          currentValue.values.forEach(v => {
            const updatedCount = e[1].find(nv => nv.value === v.value);
            if (updatedCount) {
              v.count = updatedCount.count;
            }
          });
          return currentValue;
        }
        return <FacetStats>{
          name: e[0],
          values: this.filterFacetValues ? this.filterFacetValues(e[0], e[1]) : e[1],
          nameFormatter: this.nameFormatter,
          valueFormatter: this.getValueFormatter ? this.getValueFormatter(e[0]) : null,
          valuesSelectedCallback: this.getValuesSelectedCallback ? this.getValuesSelectedCallback(e[0]) : null
        };
      });
    this.facets = lodash.sortBy(this.allFacets
      .filter(e => !this.hiddenFacets.includes(e.name.toLowerCase()))
      .filter(e => e.values.length > 0)
      .filter(e => !this.getHiddenFacets().includes(e.name)), f => this.facetOrdering.indexOf(f.name));
    this.otherFacets = this.facets.filter(f => this.isOtherFacet(f))
      .filter(f => !FacetUtils.isBooleanFacet(f) || (f.values.find(v => v.value === "true")?.count || 0) > 0);
    this.facets = this.facets.filter(f => !this.isOtherFacet(f));
  }

  private isOtherFacet(f: FacetStats) {
    return FacetUtils.isBooleanFacet(f) || this.otherFacetNames.includes(f.name);
  }

  protected searchTermFunction = (term: string): Observable<any> => {
    if (!term?.length || term?.length >= this.minCharactersTerm) {
      this.searchTermChanged(term);
      return this.getData(term).pipe(tap(d => this.getFacets(term).subscribe(f => this.setFacetsFromResult(f.facets))));
    }
  }

  protected searchTermChanged = (term: string): void => {
    this.from = 0;
    this.endReached = false;
    // Do NOT eagerly clear `this.data` here. Clearing it synchronously blanks the list to 0 rows for
    // the frame(s) between this handler firing and the debounced getData/renderRecords repopulating it,
    // which is the "list-blank-while-typing" flicker. Instead we keep the previous rows visible (with
    // the query spinner, getData runs with {showSpinner: true}) until renderRecords(result, false)
    // REPLACES them with a fresh array. Correctness is preserved: an empty result still ends in the
    // empty-state (renderRecords sets data to [] -> isEmpty once loading clears), clearing the term
    // still reloads the full list, `from` is still reset to 0 for pagination, and append=false fully
    // replaces the rows so no stale results from a previous term survive.
    // Replace the overviewFilters reference instead of mutating it in place, so app-overview's
    // overviewFilters @Input receives a NEW object. This mirrors the immutable selectedIds idiom in
    // this same base class and is what lets app-overview move to OnPush (Phase 2) without the term
    // update going unnoticed by change detection.
    this.filters = {
      ...this.filters,
      overviewFilters: {
        ...this.filters.overviewFilters,
        term,
        selectedQuickView: this.resolveQuickViewForSearchTerm(term)
      }
    };
  }

  protected facetFiltersChanged(overviewFilters: OverviewFilters<any>) {
    this.filters.overviewFilters = overviewFilters;
    this.saveFiltersInLocalStorage(this.filters);
    this.loadData();
  }

  protected declarationDateChanged = (date: DateFieldRange) => {
    if (date) {
      this.filters.overviewFilters.filters.timeRange = <DateFieldRange>{
        start: moment(date.start).toISOString(),
        end: moment(date.end).toISOString()
      }
    } else {
      this.updateTimeRangeToDefault(this.filters.overviewFilters.term);
    }
    this.data = [];
    this.loadData();
  }

  protected updateTimeRangeToDefault(term: string) {
    const defaultRange = this.ranges.find(r => r.default);
    this.filters.overviewFilters.filters.timeRange = term ? null : defaultRange ? {
      start: defaultRange.start.toISOString(),
      end: defaultRange.end.toISOString(),
      label: defaultRange.label
    } : null;
  }

  protected clearFilters(overviewFilters: OverviewFilters<any>) {
    this.updateTimeRangeToDefault(overviewFilters.term);
    overviewFilters.selectedQuickView = this.filters.overviewFilters.selectedQuickView;
    this.filters.overviewFilters = overviewFilters;
    this.saveFiltersInLocalStorage(this.filters);
    this.loadData();
  }

  protected loadNextPage = () => {
    if (!this.endReached && !this.loading) {
      this.from += this.maxItems;
      this.loadData(true, false);
    }
  }

  protected loadData = (append: boolean = false, loadFacets: boolean = true) => {
    if (!append) {
      this.from = 0;
      this.endReached = false;
    }
    this.hasActiveFacetFilters = FacetUtils.hasActiveFilters(this.filters.overviewFilters);
    this.getData(this.filters.overviewFilters.term).subscribe(result => this.renderRecords(result, append));
    if (loadFacets) {
      // getFacets() itself already triggers the shared RequestGateway toast/Sentry capture on a real
      // HTTP failure (see request-gateway.ts's `tap({error: ...})`), but this subscribe previously had
      // no error branch at all: on error, `facetsLoading` stayed stuck `true` and `facets`/`allFacets`
      // stayed `undefined` forever with nothing in the log to tie that state back to "facets failed" -
      // an empty sidebar and a 0 count with no clue why. Restore a normal loading state and log the
      // failure with enough context (query type) to diagnose it, without duplicating the existing
      // generic error toast/Sentry event.
      this.getFacets(this.filters.overviewFilters.term).subscribe({
        next: (facets: FacetResult) => this.setFacetsFromResult(facets.facets),
        error: (error) => {
          this.facetsLoading = false;
          console.error("Failed to load facets for query", this.getDataQuery(this.filters.overviewFilters.term).className, error);
        }
      });
    }
  }

  private getFiltersFromLocalStorage = (): FILTERS => {
    if (this.localStorageKey) {
      return this.supportBackwardsCompatibility(lodash.merge(<FILTERS>{overviewFilters: FacetUtils.defaultOverviewFilters()},
        JSON.parse(localStorage.getItem(this.localStorageKey))));
    } else {
      return <FILTERS>{overviewFilters: FacetUtils.defaultOverviewFilters()};
    }
  }

  protected saveFiltersInLocalStorage = (filters: FILTERS) => {
    if (this.localStorageKey) {
      localStorage.setItem(this.localStorageKey, JSON.stringify(filters));
    }
  }

  private supportBackwardsCompatibility = (filters: FILTERS): FILTERS => {
    if (!filters) {
      return filters;
    }
    const term = filters.overviewFilters?.term || filters["term"];
    if (filters["facetFilters"]) {
      filters.overviewFilters = filters["facetFilters"];
      delete filters["facetFilters"];
    }
    if (term) {
      filters.overviewFilters.term = '';
      delete filters["term"];
    }
    filters.overviewFilters.filters = filters.overviewFilters.filters || {};
    return filters;
  }

  // Snapshots the pre-search filters the first time a non-empty term is entered (so the originally
  // selected quick view can be restored once the term is cleared) and resolves which quick view a
  // search should run under: the first quick view while a term is active, or the restored original
  // once the term is cleared. This only manages the overviewFiltersBeforeSearch snapshot as a side
  // effect - it never mutates this.filters, so searchTermChanged can build a fresh, immutable
  // overviewFilters object. Behaviour matches the previous in-place backupFiltersBeforeSearch.
  private resolveQuickViewForSearchTerm = (term: string): QuickView => {
    if (!this.overviewFiltersBeforeSearch && term?.length) {
      this.overviewFiltersBeforeSearch = cloneDeep(this.filters);
      return this.quickViews[0];
    }
    if (!term?.length && this.overviewFiltersBeforeSearch) {
      const originalQuickView = this.overviewFiltersBeforeSearch.overviewFilters.selectedQuickView;
      this.overviewFiltersBeforeSearch = null;
      return originalQuickView;
    }
    return this.filters.overviewFilters.selectedQuickView;
  }

  private getFacets(term: string) {
    const query = this.getDataQuery(term);
    this.facetsLoading = true;
    return sendFacetsQuery(query.className, query.payload).pipe(tap(() => this.facetsLoading = false));
  }

  protected getFacetValues(facetName: string): Observable<FacetValueResult[]> {
    const query = this.getDataQuery(this.filters?.overviewFilters?.term);
    if (query.payload.facetFilters?.length) {
      query.payload.facetFilters = (query.payload.facetFilters as FacetFilter[]).filter(f => f.facetName !== facetName);
    }
    this.facetsLoading = true;
    return sendFacetsQuery(query.className, query.payload).pipe(tap(() => this.facetsLoading = false))
      .pipe(map(facets => lodash.isEmpty(facets?.facets) ? null : facets.facets[facetName]));
  }

  protected prepareDateTimeRange(dateFieldRange: DateFieldRange, force: boolean, defaultRange: DateTimeRange): DateTimeRange {
    const input = cloneDeep(dateFieldRange);

    if (input) {
      input.end = moment(input.end).add(1, "day").toISOString();

      return input;
    } else if (force) {
      return defaultRange;
    } else {
      return null;
    }
  }
}

export interface DataQuery<T> {
  className: string;
  payload: T;
}
